import noteIcon from 'assets/icons/noteIcon.svg';
import submitIcon from 'assets/icons/submitIcon.svg';
import { notesInterface } from 'Interfaces/FDPInterfaces/tmsInterfaces';
import { observer } from 'mobx-react';
import { Avatar } from 'primereact/avatar';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tooltip } from 'primereact/tooltip';
import { useEffect, useState } from 'react';
import { convertIsoToDateString } from 'utils/dateUtilities';
import { getInitials } from 'utils/getInitials';
import classes from './notes.module.scss';
import notesBg from 'assets/icons/edges.svg';
import { getBackgroundColor } from 'utils/avatarUtilities';
import NotesLoadingSkeleton from '../NotesLoaderSkeleton';
import { Skeleton } from 'primereact/skeleton';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';

const NotesSection = ({
  notesData,
  isNotesDataLoading,
  mode,
  isNoteAddLoading = false,
  isAddNewButtonDisabled = true,
  editLoading,
  deleteLoading,
  setNoteData = () => {},
  removeNoteData = () => {},
  handleAddNote = () => {},
  handleEditNote = () => {},
  handleDeleteNote = () => {},
}: {
  isAddNewButtonDisabled?: boolean;
  notesData: notesInterface[];
  isNotesDataLoading: boolean;
  mode: 'view' | 'edit';
  isNoteAddLoading?: boolean;
  editLoading?: Map<number, boolean>;
  deleteLoading?: Map<number, boolean>;
  setNoteData?: (idx: number, note: string) => void;
  removeNoteData?: (idx: number) => void;
  handleAddNote?: (comment: string) => void;
  handleEditNote?: (id: number, note: string) => void;
  handleDeleteNote?: (id: number) => void;
}) => {
  const [value, setValue] = useState<string>('');
  const [toggleNote, setToggleNote] = useState<boolean>(false);
  const [toggleInput, setToggleInput] = useState<Set<number>>(new Set());
  const [isConfirmationVisible, setIsConfirmationVisible] = useState<boolean>(false);
  const [deletingNote, setDeletingNote] = useState<notesInterface | null>(null);
  const [deletingNoteIndex, setDeletingNoteIndex] = useState<number>(-1);

  const handleCancel = () => {
    setValue('');
    setToggleNote(false);
  };

  const handleToggle = (id: number) => {
    setToggleInput(prev => {
      const newSet = new Set(prev); // Create a new Set instance
      if (newSet.has(id)) {
        newSet.delete(id); // Remove the ID if it exists
      } else {
        newSet.add(id); // Add the ID if it doesn't exist
      }
      return newSet; // Return the new Set
    });
  };

  useEffect(() => {
    if (!isNoteAddLoading) {
      setValue('');
    }
  }, [isNoteAddLoading]);

  return (
    <>
      {isNotesDataLoading ? (
        <NotesLoadingSkeleton />
      ) : (
        <div className={classes.notesContainer}>
          <>
            <p className={classes.notesHeading}>Notes</p>
            {mode === 'edit' && (
              <div className={classes.noteInpContainer}>
                {!toggleNote ? (
                  <Button
                    className={classes.noteSubmitBtn}
                    outlined
                    style={{ display: 'flex', gap: '0.5rem' }}
                    onClick={() => setToggleNote(true)}
                    disabled={isAddNewButtonDisabled}
                  >
                    Add Note <img src={noteIcon} alt="icon" />
                  </Button>
                ) : (
                  <>
                    <InputTextarea
                      autoResize
                      value={value}
                      placeholder="Note..."
                      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                        setValue(e.target.value)
                      }
                      rows={5}
                      cols={40}
                      disabled={isNoteAddLoading}
                    />
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '0.5rem',
                      }}
                    >
                      <Button
                        className={classes.noteSubmitBtn}
                        outlined
                        style={{
                          display: 'flex',
                          gap: '0.5rem',
                          color: '#028383',
                        }}
                        onClick={() => {
                          handleAddNote(value);
                        }}
                        loading={isNoteAddLoading}
                        disabled={!value}
                      >
                        Submit <img src={submitIcon} alt="icon" />
                      </Button>

                      <Button
                        className={classes.noteSubmitBtn}
                        outlined
                        style={{ display: 'flex', gap: '0.5rem', color: 'red' }}
                        onClick={handleCancel}
                        disabled={isNoteAddLoading}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            <div className={classes.prevNotesContainer}>
              {notesData.length ? (
                <>
                  {notesData.map((item, index) => {
                    return (
                      <div key={index}>
                        <div className={classes.noteSection}>
                          <span style={{ marginBottom: '0px', cursor: 'pointer' }}>
                            <Tooltip
                              target={`#profileAvatar-${index}`}
                              content={item.CreatedBy}
                              mouseTrack
                              mouseTrackLeft={10}
                              position="bottom"
                            />
                            <Avatar
                              title={item.CreatedBy}
                              id={`profileAvatar-${index}`}
                              shape="circle"
                              label={getInitials(item.CreatedBy)}
                              style={{
                                backgroundColor: getBackgroundColor(item.CreatedBy[0]),
                                color: '#522D6E',
                                marginRight: '0.5rem',
                              }}
                            />
                          </span>
                          <div className={classes.noteContainer}>
                            <div className={classes.notesSvg}>
                              <img src={notesBg} alt="notesicon" />
                            </div>
                            <div className={classes.noteElement}>
                              <div className={classes.noteBox}>
                                {toggleInput.has(item.ID) ? (
                                  <InputTextarea
                                    value={item.Notes}
                                    cols={30}
                                    rows={3}
                                    onChange={e => setNoteData(index, e.target.value)}
                                  />
                                ) : (
                                  <p>{item.Notes}</p>
                                )}
                                {mode === 'edit' && (
                                  <div className={classes.noteBtnSection}>
                                    <Button
                                      icon="pi pi-trash"
                                      className={classes.btn}
                                      outlined
                                      style={{ color: 'red' }}
                                      loading={deleteLoading && deleteLoading.get(item.ID)}
                                      onClick={() => {
                                        setDeletingNote(item);
                                        setDeletingNoteIndex(index);
                                        setIsConfirmationVisible(prev => !prev);
                                      }}
                                    />
                                    {!toggleInput.has(item.ID) ? (
                                      <Button
                                        icon="pi pi-pencil"
                                        className={classes.btn}
                                        outlined
                                        style={{ color: '#5A5959' }}
                                        loading={editLoading && editLoading.get(item.ID)}
                                        onClick={() => handleToggle(item.ID)}
                                      />
                                    ) : (
                                      <Button
                                        icon="pi pi-check"
                                        className={classes.btn}
                                        outlined
                                        style={{ color: '#028383' }}
                                        onClick={async () => {
                                          handleEditNote(item.ID, item.Notes);
                                          handleToggle(item.ID);
                                        }}
                                      />
                                    )}
                                  </div>
                                )}

                                <span className={classes.notedateContainer}>
                                  {editLoading && editLoading.get(item.ID) ? (
                                    <Skeleton height="1rem" width="10rem" />
                                  ) : (
                                    convertIsoToDateString(item.CreatedDate)
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <div>No Note is added yet</div>
              )}
            </div>
          </>
        </div>
      )}

      <ConfirmationModal
        visibility={isConfirmationVisible}
        modalHeading="Confirmation"
        modalContent={'Are you sure want to delete the note?'}
        loading={(deletingNote && deleteLoading && deleteLoading.get(deletingNote?.ID)) || false}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        handleCancel={() => {
          setIsConfirmationVisible(false);
          setDeletingNote(null);
        }}
        handleConfirm={async () => {
          if (deletingNote) {
            handleDeleteNote(deletingNote?.ID);
            removeNoteData(deletingNoteIndex);
            setIsConfirmationVisible(false);
          }
        }}
      />
    </>
  );
};

export default observer(NotesSection);
